import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, X, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './Chatbot.css';

export default function Chatbot() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const currentLang = (i18n.language || 'es').substring(0, 2);

  const initialText = currentLang === 'en'
    ? 'Hello! 👋 I am the Garaoke assistant. Do you have any questions about hosting your karaoke party?'
    : '¡Hola! 👋 Soy el asistente de Garaoke. ¿Tenés alguna duda sobre cómo armar tu fiesta de karaoke?';

  const [messages, setMessages] = useState([
    { from: 'bot', text: initialText }
  ]);
  const [input, setInput] = useState('');
  const messagesEnd = useRef(null);

  // Actualizar saludo si cambia el idioma y no hay interacción
  useEffect(() => {
    setMessages([
      { from: 'bot', text: t('chatbot.initialMsg', initialText) }
    ]);
  }, [i18n.language, t]);

  const botResponsesEs = {
    precio: '¡Tenemos planes desde $0/mes! El plan Gratis te permite disfrutar de canciones y playlists. Los planes Pro y Premium suman interactividad en vivo con celulares, intros personalizadas y efectos de sonido.',
    plan: 'Podés empezar gratis y actualizar a Pro o Premium cuando quieras desde la sección de Planes.',
    funciona: 'Es súper simple: 1) Entrás a la app 2) Buscás tus temas favoritos de YouTube o cargás una playlist 3) Abrís la pantalla de reproducción para tu TV 4) ¡Tus invitados escanean el QR con su celular y participan!',
    tv: 'Conectás tu compu a la TV por HDMI o Chromecast y hacés click en el botón de TV en la barra superior. Se abrirá una pantalla secundaria limpia y a pantalla completa.',
    qr: 'El código QR aparece en pantalla para que cualquiera en la fiesta lo escanee desde su celular sin instalar ninguna app, pudiendo ver la lista y votar.',
    celular: '¡Sí! No hace falta descargar nada. Funciona desde el navegador de cualquier teléfono.',
    youtube: 'Podés buscar cualquier canción de karaoke en YouTube en tiempo real con soporte para tus propias API keys en Configuración.',
    default: '¡Excelente consulta! Te recomiendo probar la app gratis con el botón "Comenzar Gratis" o revisar nuestra sección de Preguntas Frecuentes para más detalles.'
  };

  const botResponsesEn = {
    price: 'We have plans starting at $0/month! The Free plan lets you enjoy songs and playlists. Pro and Premium plans add live phone interactivity, custom intros, and sound effects.',
    plan: 'You can start for free and upgrade to Pro or Premium anytime from the Pricing section.',
    how: 'It is super easy: 1) Enter the app 2) Search favorite YouTube tracks or load a playlist 3) Open the TV screen 4) Your guests scan the QR and join in!',
    work: 'It is super easy: 1) Enter the app 2) Search favorite YouTube tracks or load a playlist 3) Open the TV screen 4) Your guests scan the QR and join in!',
    tv: 'Connect your laptop to the TV via HDMI or Chromecast and click the TV icon on top. A clean fullscreen secondary window will open.',
    qr: 'The QR code appears on screen so anyone can scan it with their phone camera without downloading any app to vote and request songs.',
    phone: 'Yes! No app installation needed. It works on any mobile browser.',
    youtube: 'You can search any karaoke track on YouTube in real time with support for custom API keys in Settings.',
    default: 'Great question! I recommend trying the app for free with the "Start for Free" button or checking our FAQ section for more details.'
  };

  const handleSend = () => {
    if (!input.trim()) return;
    const userMsg = input.trim();
    setMessages(prev => [...prev, { from: 'user', text: userMsg }]);
    setInput('');

    setTimeout(() => {
      const lower = userMsg.toLowerCase();
      const responses = currentLang === 'en' ? botResponsesEn : botResponsesEs;
      let response = responses.default;

      for (const [key, val] of Object.entries(responses)) {
        if (key !== 'default' && lower.includes(key)) {
          response = val;
          break;
        }
      }
      setMessages(prev => [...prev, { from: 'bot', text: response }]);
    }, 500);
  };

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <>
      {/* Botón flotante toggle con animación de rebote sutil cuando está cerrado */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`chatbot-toggle ${!isOpen ? 'chatbot-toggle--bounce' : ''}`}
        aria-label="Abrir asistente de chat"
        id="chatbot-toggle-btn"
      >
        {isOpen ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}
      </button>

      {/* Ventana de chat */}
      {isOpen && (
        <div className="chatbot-window">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-avatar">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="chatbot-title">{t('chatbot.title', 'Asistente Garaoke')}</p>
              <p className="chatbot-status">{t('chatbot.status', 'Online en vivo')}</p>
            </div>
          </div>

          {/* Lista de Mensajes */}
          <div className="chatbot-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`chatbot-msg-row ${msg.from === 'user' ? 'chatbot-msg-row--user' : 'chatbot-msg-row--bot'}`}>
                <div className={`chatbot-bubble ${msg.from === 'user' ? 'chatbot-bubble--user' : 'chatbot-bubble--bot'}`}>
                  {msg.text}
                </div>
              </div>
            ))}
            <div ref={messagesEnd} />
          </div>

          {/* Input */}
          <div className="chatbot-input-area">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder={t('chatbot.placeholder', 'Preguntá lo que quieras...')}
              className="chatbot-input"
            />
            <button onClick={handleSend} className="chatbot-send-btn" aria-label="Enviar mensaje">
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
